import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays, differenceInCalendarDays, eachDayOfInterval, endOfMonth, format,
  isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek,
} from "date-fns";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth, type Department } from "@/lib/auth-context";
import {
  fetchCalendarEntries, COLOR_TAG_HEX, DEPT_KEYS, DEPT_KEY_LABEL,
  ENTRY_TYPES, classifyEntry, KIND_CLASSES, KIND_DOT, markCalendarEntryComplete,
  deleteCalendarEntry,
  type CalendarEntryWithRelations, type EntryType, type EntryKind, type EntryPriority,
} from "@/lib/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddCalendarEntryModal } from "@/components/calendar/AddCalendarEntryModal";
import { EditCalendarEntryModal } from "@/components/calendar/EditCalendarEntryModal";
import { ManageHolidaysModal } from "@/components/calendar/ManageHolidaysModal";
import { fetchOfficeHolidays, HOLIDAY_TYPE_LABEL, type OfficeHoliday } from "@/lib/office-holidays";
import { sendCalendarReminder } from "@/lib/calendar-reminder.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/calendar")({ component: CalendarPage });

const LAYERS: { key: string; label: string; types: EntryType[] }[] = [
  { key: "holidays", label: "Global Holidays", types: ["holiday"] },
  { key: "festivals", label: "Indian Festivals", types: ["festival"] },
  { key: "launches", label: "Product Launches", types: ["launch"] },
  { key: "campaigns", label: "Marketing Campaigns", types: ["campaign"] },
  { key: "smm", label: "SMM Posting", types: ["posting"] },
  { key: "seo", label: "SEO Deadlines", types: ["deadline"] },
  { key: "editors", label: "Editor Deliverables", types: ["review"] },
  { key: "calling", label: "Calling Sprints", types: ["task"] },
  { key: "hr", label: "HR Events", types: ["payroll", "meeting"] },
];

type FallbackSpec = {
  id: string;
  title: string;
  start_date: string;
  entry_type: EntryType;
  departments: Department[];
  color_tag: keyof typeof COLOR_TAG_HEX;
  visibility_scope: "global" | "department" | "personal";
};

const FALLBACK_SPECS: FallbackSpec[] = [
  { id: "fb-1", title: "Labour Day", start_date: "2026-05-01", entry_type: "holiday", departments: [], color_tag: "gray", visibility_scope: "global" },
  { id: "fb-2", title: "Script Deadline: BHC Batch", start_date: "2026-05-05", entry_type: "deadline", departments: ["scriptwriter"], color_tag: "orange", visibility_scope: "department" },
  { id: "fb-3", title: "AB Call Sprint Starts", start_date: "2026-05-08", entry_type: "campaign", departments: ["calling"], color_tag: "blue", visibility_scope: "department" },
  { id: "fb-4", title: "Hanuman Jayanti Campaign", start_date: "2026-05-12", entry_type: "festival", departments: ["smm", "marketer"], color_tag: "purple", visibility_scope: "global" },
  { id: "fb-5", title: "Editor Delivery: 20 Reels", start_date: "2026-05-15", entry_type: "deadline", departments: ["editor"], color_tag: "orange", visibility_scope: "department" },
  { id: "fb-6", title: "SEO Blog Publish", start_date: "2026-05-18", entry_type: "deadline", departments: ["seo"], color_tag: "green", visibility_scope: "department" },
  { id: "fb-7", title: "Anti-slip Tape Launch", start_date: "2026-05-20", entry_type: "launch", departments: [], color_tag: "black", visibility_scope: "global" },
  { id: "fb-8", title: "Influencer Go-live: Apron", start_date: "2026-05-22", entry_type: "posting", departments: ["smm"], color_tag: "pink", visibility_scope: "department" },
  { id: "fb-9", title: "Meta A/B Test Window", start_date: "2026-05-25", entry_type: "campaign", departments: ["marketer"], color_tag: "orange", visibility_scope: "department" },
  { id: "fb-10", title: "Payroll Processing Day", start_date: "2026-05-28", entry_type: "payroll", departments: ["hr"], color_tag: "gray", visibility_scope: "department" },
];

const FALLBACK_ENTRIES: CalendarEntryWithRelations[] = FALLBACK_SPECS.map((s) => ({
  id: s.id,
  title: s.title,
  description: null,
  entry_type: s.entry_type,
  date_type: "single_day",
  start_date: s.start_date,
  end_date: null,
  linked_project: null,
  reminder_type: "none",
  recurring_type: "none",
  color_tag: s.color_tag,
  visibility_scope: s.visibility_scope,
  created_by: "fallback",
  created_at: new Date().toISOString(),
  is_active: true,
  departments: s.departments,
  assignees: [],
}));

function CalendarPage() {
  const { profile, role, user } = useAuth();
  const isAdminMode = role === "admin" || role === "moderator";
  const isHr = profile?.department === "hr";
  const canManageHolidays = role === "admin" || isHr;
  const dept = profile?.department;
  const userId = user?.id ?? null;
  const [view, setView] = useState<"month" | "week" | "list">(isAdminMode ? "month" : "week");
  const [deptFilter, setDeptFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [layersOn, setLayersOn] = useState<Record<string, boolean>>(
    Object.fromEntries(LAYERS.map((l) => [l.key, true])),
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [holidaysModalOpen, setHolidaysModalOpen] = useState(false);
  const [drawerDate, setDrawerDate] = useState<Date | null>(null);

  const { data: dbEntries = [] } = useQuery({
    queryKey: ["calendar-entries"],
    queryFn: fetchCalendarEntries,
  });

  const calendarYear = useMemo(() => new Date().getFullYear(), []);
  const { data: officeHolidays = [] } = useQuery({
    queryKey: ["office-holidays-calendar", calendarYear],
    queryFn: () => fetchOfficeHolidays(calendarYear),
  });

  const holidayEntries: CalendarEntryWithRelations[] = useMemo(
    () => officeHolidays.map((h) => ({
      id: `holiday-${h.id}`,
      title: h.name,
      description: HOLIDAY_TYPE_LABEL[h.holiday_type],
      entry_type: "holiday",
      date_type: "single_day",
      start_date: h.holiday_date,
      end_date: null,
      linked_project: null,
      reminder_type: "none",
      recurring_type: "none",
      color_tag: "gray",
      visibility_scope: "global",
      created_by: h.created_by ?? "system",
      created_at: h.created_at,
      is_active: true,
      departments: [],
      assignees: [],
    })),
    [officeHolidays],
  );

  const allEntries = useMemo(
    () => [...holidayEntries, ...(dbEntries.length > 0 ? dbEntries : FALLBACK_ENTRIES)],
    [dbEntries, holidayEntries],
  );

  // Visibility filter (server enforces too)
  const visibleEntries = useMemo(() => {
    if (isAdminMode) return allEntries;
    if (!dept) return allEntries.filter((e) => e.visibility_scope === "global");
    return allEntries.filter((e) => {
      if (e.visibility_scope === "global") return true;
      if (e.departments.includes(dept)) return true;
      if (e.assignees.some((a) => a.user_id === profile?.id)) return true;
      if (e.assignees.some((a) => a.department === dept)) return true;
      return false;
    });
  }, [allEntries, isAdminMode, dept, profile?.id]);

  const filtered = useMemo(() => {
    return visibleEntries.filter((e) => {
      if (deptFilter !== "all" && !e.departments.includes(deptFilter as Department)) return false;
      if (typeFilter !== "all" && e.entry_type !== typeFilter) return false;
      return true;
    });
  }, [visibleEntries, deptFilter, typeFilter]);

  const today = new Date();
  const [cursor, setCursor] = useState<Date>(startOfMonth(today));
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const leadingBlanks = (monthStart.getDay() + 6) % 7;

  const weekStart = startOfWeek(view === "week" ? cursor : today, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const goPrev = () =>
    setCursor((c) => (view === "week" ? addDays(c, -7) : startOfMonth(addDays(startOfMonth(c), -1))));
  const goNext = () =>
    setCursor((c) => (view === "week" ? addDays(c, 7) : startOfMonth(addDays(endOfMonth(c), 1))));
  const goToday = () => setCursor(view === "week" ? today : startOfMonth(today));

  const headingTitle = isAdminMode ? "Calendar" : `${DEPT_KEY_LABEL[dept ?? "editor"]} Calendar`;
  const subtitle = isAdminMode
    ? `${format(today, "MMMM yyyy")} · All departments + global events`
    : `Week of ${format(weekDays[0], "dd")}–${format(weekDays[6], "dd MMM yyyy")} · Tasks assigned to ${DEPT_KEY_LABEL[dept ?? "editor"]} and tasks created by ${DEPT_KEY_LABEL[dept ?? "editor"]}`;

  return (
    <>
      <AppHeader subtitle={subtitle} />
      <div className="px-[22px] pb-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-[24px] font-bold">{headingTitle}</h1>
            {!isAdminMode && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {DEPT_KEY_LABEL[dept ?? "editor"]} only
              </span>
            )}
          </div>
        </div>

        {/* Controls row */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-md border border-border bg-card p-0.5">
            {(["month", "week", "list"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "rounded px-3 py-1 text-[11px] font-medium capitalize transition-colors",
                  view === v ? "bg-foreground text-background" : "text-muted-foreground",
                )}
              >
                {v}
              </button>
            ))}
          </div>

          {view !== "list" && (
            <div className="flex items-center gap-1">
              <button
                onClick={goPrev}
                className="rounded-md border border-border bg-card px-2 py-1 text-[12px] hover:bg-muted"
                aria-label="Previous"
              >
                ‹
              </button>
              <div className="min-w-[120px] rounded-md border border-border bg-card px-3 py-1 text-center text-[11px] font-semibold">
                {view === "week"
                  ? `${format(weekDays[0], "dd MMM")} – ${format(weekDays[6], "dd MMM yyyy")}`
                  : format(cursor, "MMMM yyyy")}
              </div>
              <button
                onClick={goNext}
                className="rounded-md border border-border bg-card px-2 py-1 text-[12px] hover:bg-muted"
                aria-label="Next"
              >
                ›
              </button>
              <button
                onClick={goToday}
                className="ml-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] hover:bg-muted"
              >
                Today
              </button>
            </div>
          )}

          {isAdminMode && (
            <>
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="h-8 w-[160px] text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {DEPT_KEYS.map((d) => (
                    <SelectItem key={d} value={d}>{DEPT_KEY_LABEL[d]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-8 w-[140px] text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {ENTRY_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button asChild size="sm" className="bg-foreground text-background hover:bg-foreground/90">
              <Link to="/calendar/intelligence">Calendar Intelligence →</Link>
            </Button>
            {canManageHolidays && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setHolidaysModalOpen(true)}
                className="border-foreground text-foreground hover:bg-foreground hover:text-background"
              >
                Manage Holidays
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setModalOpen(true)}
              className="bg-violet-600 text-white hover:bg-violet-700"
            >
              + Add {isAdminMode ? "Entry" : `${DEPT_KEY_LABEL[dept ?? "editor"]} Entry`}
            </Button>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="rounded-xl border border-border bg-card shadow-sm">
            {view === "month" && (
              <MonthView
                days={monthDays}
                entries={filtered}
                monthRef={cursor}
                leadingBlanks={leadingBlanks}
                userId={userId}
                userDept={dept ?? null}
                onDayClick={(d) => setDrawerDate(d)}
              />
            )}
            {view === "week" && (
              <WeekView days={weekDays} entries={filtered} userId={userId} userDept={dept ?? null} />
            )}
            {view === "list" && (
              <ListView entries={filtered} userId={userId} userDept={dept ?? null} />
            )}
          </div>

          <div className="space-y-4">
            {isAdminMode ? (
              <>
                <LayersCard layersOn={layersOn} setLayersOn={setLayersOn} />
                <UpcomingDeadlines entries={filtered} />
              </>
            ) : (
              <UserSidePanel
                entries={filtered}
                officeHolidays={officeHolidays}
                userId={userId}
                userDept={dept ?? null}
              />
            )}
          </div>
        </div>
      </div>

      <DayDrawer
        date={drawerDate}
        entries={drawerDate ? filtered.filter((e) => isSameDay(parseISO(e.start_date), drawerDate)) : []}
        officeHolidays={officeHolidays}
        userId={userId}
        userDept={dept ?? null}
        onClose={() => setDrawerDate(null)}
      />

      <AddCalendarEntryModal open={modalOpen} onOpenChange={setModalOpen} />
      {canManageHolidays && (
        <ManageHolidaysModal open={holidaysModalOpen} onOpenChange={setHolidaysModalOpen} />
      )}
    </>
  );
}

// ============================================================
// PART A — VIEWS WITH COLOR CODING
// ============================================================

function EntryPill({
  entry, userId, userDept, compact = false,
}: {
  entry: CalendarEntryWithRelations;
  userId: string | null;
  userDept: Department | null;
  compact?: boolean;
}) {
  const kind: EntryKind = classifyEntry(entry, userId, userDept);
  const isUrgent = entry.priority === "urgent";
  const isAssigned = kind === "assigned";

  return (
    <div className={cn("relative rounded border px-1.5 py-1 leading-tight", KIND_CLASSES[kind])}>
      {isUrgent && (
        <span className="absolute right-1 top-1 inline-block h-1.5 w-1.5 rounded-full bg-red-600" />
      )}
      <div className={cn("truncate font-semibold", compact ? "text-[9px]" : "text-[11px]")}>
        {entry.title}
      </div>
      {isAssigned && entry.assigner && (
        <div className={cn("truncate opacity-75", compact ? "text-[8px]" : "text-[10px]")}>
          From: {entry.assigner.full_name} · {DEPT_KEY_LABEL[entry.assigner.department]}
        </div>
      )}
      {!isAssigned && !compact && entry.departments[0] && (
        <div className="truncate text-[10px] opacity-70">
          {entry.departments.map((d) => DEPT_KEY_LABEL[d]).join(", ")}
        </div>
      )}
    </div>
  );
}

function MonthView({
  days, entries, monthRef, leadingBlanks, userId, userDept, onDayClick,
}: {
  days: Date[];
  entries: CalendarEntryWithRelations[];
  monthRef: Date;
  leadingBlanks: number;
  userId: string | null;
  userDept: Department | null;
  onDayClick: (d: Date) => void;
}) {
  return (
    <div>
      <div className="flex items-end justify-between border-b border-border px-4 pb-3 pt-4">
        <div>
          <div className="text-[18px] font-bold leading-none text-[#18181B]">
            {format(monthRef, "MMMM yyyy")}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">Click a day for details</div>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-border">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} className="min-h-[110px] border-b border-r border-border bg-muted/20" />
        ))}
        {days.map((day, i) => {
          const dayEntries = entries.filter((e) => isSameDay(parseISO(e.start_date), day));
          const inMonth = isSameMonth(day, monthRef);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(day)}
              className={cn(
                "min-h-[110px] cursor-pointer border-b border-r border-border p-1.5 text-left transition-colors hover:bg-muted/40",
                !inMonth && "bg-muted/30",
              )}
            >
              <div className="text-[10px] font-semibold text-muted-foreground">
                {format(day, "dd")}
              </div>
              <div className="mt-1 space-y-1">
                {dayEntries.slice(0, 3).map((e) => (
                  <EntryPill key={e.id} entry={e} userId={userId} userDept={userDept} compact />
                ))}
                {dayEntries.length > 3 && (
                  <div className="text-[9px] text-muted-foreground">+{dayEntries.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({
  days, entries, userId, userDept,
}: {
  days: Date[];
  entries: CalendarEntryWithRelations[];
  userId: string | null;
  userDept: Department | null;
}) {
  return (
    <div>
      <div className="grid grid-cols-7 border-b border-border">
        {days.map((d) => (
          <div key={d.toISOString()} className="px-3 py-2 text-[11px] font-semibold">
            {format(d, "EEE dd")}
          </div>
        ))}
      </div>
      <div className="grid min-h-[420px] grid-cols-7">
        {days.map((d) => {
          const dayEntries = entries.filter((e) => isSameDay(parseISO(e.start_date), d));
          return (
            <div key={d.toISOString()} className="space-y-2 border-r border-border p-2">
              {dayEntries.map((e) => (
                <EntryPill key={e.id} entry={e} userId={userId} userDept={userDept} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ListView({
  entries, userId, userDept,
}: {
  entries: CalendarEntryWithRelations[];
  userId: string | null;
  userDept: Department | null;
}) {
  return (
    <ul className="divide-y divide-border">
      {entries.length === 0 && (
        <li className="px-4 py-6 text-center text-[12px] text-muted-foreground">No entries.</li>
      )}
      {entries.map((e) => {
        const kind = classifyEntry(e, userId, userDept);
        const isAssigned = kind === "assigned";
        return (
          <li key={e.id} className={cn("flex items-center justify-between gap-3 px-4 py-3 border-l-4", KIND_CLASSES[kind].replace(/bg-\S+/, "").replace(/text-\S+/, ""))}
              style={{ borderLeftColor: KIND_DOT[kind] }}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="h-2 w-2 rounded-full" style={{ background: KIND_DOT[kind] }} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[12px] font-semibold">{e.title}</span>
                  {e.priority === "urgent" && (
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-600" title="Urgent" />
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {format(parseISO(e.start_date), "EEE, dd MMM")} · {e.entry_type}
                  {e.departments.length > 0 && ` · ${e.departments.map((d) => DEPT_KEY_LABEL[d]).join(", ")}`}
                </div>
                {isAssigned && e.assigner && (
                  <div className="text-[10px] text-purple-600">
                    From: {e.assigner.full_name} · {DEPT_KEY_LABEL[e.assigner.department]}
                  </div>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ============================================================
// PART B — NON-ADMIN SIDE PANEL
// ============================================================

function UserSidePanel({
  entries, officeHolidays, userId, userDept,
}: {
  entries: CalendarEntryWithRelations[];
  officeHolidays: OfficeHoliday[];
  userId: string | null;
  userDept: Department | null;
}) {
  const todayIST = useMemo(() => {
    const iso = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    return parseISO(iso);
  }, []);

  const todays = useMemo(
    () => entries.filter((e) => isSameDay(parseISO(e.start_date), todayIST)),
    [entries, todayIST],
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(todayIST, i)),
    [todayIST],
  );

  const nextHoliday = useMemo(() => {
    const todayStr = todayIST.toISOString().slice(0, 10);
    return officeHolidays
      .filter((h) => h.holiday_date >= todayStr)
      .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date))[0] ?? null;
  }, [officeHolidays, todayIST]);

  return (
    <>
      {/* Card 1 — Today */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Today
        </div>
        <div className="mt-1 text-[18px] font-bold text-[#18181B]">
          {format(todayIST, "EEEE, dd MMM")}
        </div>
        <div className="mt-3 space-y-1.5">
          {todays.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">Nothing scheduled today</div>
          ) : (
            todays.map((e) => {
              const kind = classifyEntry(e, userId, userDept);
              return (
                <div key={e.id} className="flex items-center gap-2 text-[12px]">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: KIND_DOT[kind] }} />
                  <span className="truncate text-foreground">{e.title}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Card 2 — This Week */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          This Week
        </div>
        <div className="mt-3 space-y-3">
          {weekDays.every((d) => entries.filter((e) => isSameDay(parseISO(e.start_date), d)).length === 0) ? (
            <div className="text-[12px] text-muted-foreground">No entries this week</div>
          ) : (
            weekDays.map((d) => {
              const dayEntries = entries.filter((e) => isSameDay(parseISO(e.start_date), d));
              if (dayEntries.length === 0) return null;
              return (
                <div key={d.toISOString()}>
                  <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                    {format(d, "EEE, dd MMM")}
                  </div>
                  <div className="mt-1 space-y-1">
                    {dayEntries.slice(0, 3).map((e) => {
                      const kind = classifyEntry(e, userId, userDept);
                      return (
                        <div key={e.id} className="flex items-center gap-2 text-[11px]">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: KIND_DOT[kind] }} />
                          <span className="truncate">{e.title}</span>
                        </div>
                      );
                    })}
                    {dayEntries.length > 3 && (
                      <div className="text-[10px] text-muted-foreground">+{dayEntries.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Card 3 — Next Holiday (dark) */}
      <div className="rounded-xl bg-[#18181B] p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-white/60">
          Next Holiday
        </div>
        <div className="mt-1 text-[18px] font-bold text-white">
          {nextHoliday?.name ?? "No upcoming holidays"}
        </div>
        {nextHoliday && (
          <div className="mt-1 text-[12px] text-white/60">
            {(() => {
              const d = parseISO(nextHoliday.holiday_date);
              const days = differenceInCalendarDays(d, todayIST);
              const label = days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days} days away`;
              return `${label} · ${format(d, "EEE, dd MMM yyyy")}`;
            })()}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================
// PART C — DAY DETAIL DRAWER
// ============================================================

const STATUS_PILL_CLS: Record<string, string> = {
  pending: "bg-amber-50 border-amber-200 text-amber-700",
  in_progress: "bg-blue-50 border-blue-200 text-blue-700",
  completed: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

function PriorityBadge({ p }: { p?: EntryPriority | null }) {
  if (p === "high") {
    return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">High</span>;
  }
  if (p === "urgent") {
    return <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">Urgent</span>;
  }
  return null;
}

function DayDrawer({
  date, entries, officeHolidays, userId, userDept, onClose,
}: {
  date: Date | null;
  entries: CalendarEntryWithRelations[];
  officeHolidays: OfficeHoliday[];
  userId: string | null;
  userDept: Department | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isAdminMod = role === "admin" || role === "moderator";
  const [editEntry, setEditEntry] = useState<CalendarEntryWithRelations | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const completeMut = useMutation({
    mutationFn: (id: string) => markCalendarEntryComplete(id),
    onSuccess: () => {
      toast.success("Marked as complete");
      qc.invalidateQueries({ queryKey: ["calendar-entries"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const open = !!date;
  const holidayMatch = useMemo(() => {
    if (!date) return null;
    const iso = format(date, "yyyy-MM-dd");
    return officeHolidays.find((h) => h.holiday_date === iso) ?? null;
  }, [date, officeHolidays]);

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCalendarEntry(id),
    onSuccess: () => {
      toast.success("Entry deleted");
      qc.invalidateQueries({ queryKey: ["calendar-entries"] });
      setDeleteId(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0">
        {date && (
          <div className="flex h-full flex-col">
            <div className="flex items-start justify-between border-b border-border p-4">
              <div>
                <div className="text-[18px] font-bold text-[#18181B]">
                  {format(date, "EEEE, dd MMMM yyyy")}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {entries.length} {entries.length === 1 ? "entry" : "entries"}
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {holidayMatch && (
              <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-[12px] font-semibold text-red-700">
                🏖️ {holidayMatch.name}
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-4">
              {entries.length === 0 ? (
                <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
                  No entries for this day
                </div>
              ) : (
                entries.map((entry) => {
                  const kind = classifyEntry(entry, userId, userDept);
                  const isAssignedToMe = !!userId && entry.assignees.some((a) => a.user_id === userId);
                  const isCreator = !!userId && entry.created_by === userId;
                  const canManage = (isCreator || isAdminMod) && !entry.id.startsWith("fb-") && !entry.id.startsWith("holiday-");
                  const status = entry.status ?? "pending";
                  const linked = entry.linked_project ?? "";
                  const isUrl = /^https?:\/\//i.test(linked);
                  return (
                    <div key={entry.id} className={cn("mb-2 rounded-lg border p-3", KIND_CLASSES[kind])}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold">{entry.title}</span>
                        <PriorityBadge p={entry.priority} />
                      </div>

                      {entry.description && (
                        <div className="mt-1 line-clamp-2 text-[12px] opacity-80">
                          {entry.description}
                        </div>
                      )}

                      {linked && (
                        <div className="mt-1 text-[11px]">
                          <span className="font-semibold opacity-70">Linked project: </span>
                          {isUrl ? (
                            <a
                              href={linked}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-700 underline underline-offset-2 hover:text-blue-900"
                            >
                              Open Link
                            </a>
                          ) : (
                            <span className="opacity-80">{linked}</span>
                          )}
                        </div>
                      )}

                      {isAssignedToMe && entry.assigner && (
                        <div className="mt-1 text-[11px] text-purple-700">
                          📌 Assigned by {entry.assigner.full_name} · {DEPT_KEY_LABEL[entry.assigner.department]}
                        </div>
                      )}

                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold", STATUS_PILL_CLS[status] ?? STATUS_PILL_CLS.pending)}>
                          {status.replace("_", " ")}
                        </span>

                        {isAssignedToMe && status !== "completed" && !entry.id.startsWith("fb-") && !entry.id.startsWith("holiday-") && (
                          <button
                            onClick={() => completeMut.mutate(entry.id)}
                            disabled={completeMut.isPending}
                            className="rounded-full bg-[#18181B] px-3 py-0.5 text-[11px] font-semibold text-white hover:opacity-80 disabled:opacity-50"
                          >
                            Mark Complete
                          </button>
                        )}

                        {canManage && (
                          <button
                            onClick={() => setEditEntry(entry)}
                            className="rounded-full border border-border bg-white px-3 py-0.5 text-[11px] font-semibold text-foreground hover:bg-muted"
                          >
                            Edit
                          </button>
                        )}

                        {canManage && (
                          <button
                            onClick={() => setDeleteId(entry.id)}
                            className="rounded-full border border-red-200 bg-white px-3 py-0.5 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
    <EditCalendarEntryModal
      open={!!editEntry}
      onOpenChange={(o) => { if (!o) setEditEntry(null); }}
      entry={editEntry}
    />
    <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              if (deleteId) deleteMut.mutate(deleteId);
            }}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {deleteMut.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ============================================================
// ADMIN SIDEBAR (unchanged)
// ============================================================

function LayersCard({
  layersOn, setLayersOn,
}: { layersOn: Record<string, boolean>; setLayersOn: (v: Record<string, boolean>) => void }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted-foreground">
        Calendar Layers
      </div>
      <div className="mt-3 space-y-2">
        {LAYERS.map((l) => (
          <label key={l.key} className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={layersOn[l.key]}
              onCheckedChange={(v) => setLayersOn({ ...layersOn, [l.key]: Boolean(v) })}
              className="data-[state=checked]:border-violet-600 data-[state=checked]:bg-violet-600"
            />
            <span>{l.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function UpcomingDeadlines({ entries }: { entries: CalendarEntryWithRelations[] }) {
  const upcoming = useMemo(() => {
    const todayMidnight = new Date(new Date().setHours(0, 0, 0, 0));
    const real = entries
      .filter((e) =>
        ["deadline", "launch", "campaign", "posting", "review", "task"].includes(e.entry_type),
      )
      .filter((e) => parseISO(e.start_date) >= todayMidnight)
      .slice(0, 8);
    if (real.length > 0) return real;
    return [
      { id: "fb-d-1", title: "Scripts due", start_date: "2026-05-05", departments: ["scriptwriter"] as Department[] },
      { id: "fb-d-2", title: "Editing done", start_date: "2026-05-12", departments: ["editor"] as Department[] },
      { id: "fb-d-3", title: "Ads ready", start_date: "2026-05-17", departments: ["marketer"] as Department[] },
      { id: "fb-d-4", title: "Influencer live", start_date: "2026-05-20", departments: ["smm"] as Department[] },
    ] as Pick<CalendarEntryWithRelations, "id" | "title" | "start_date" | "departments">[];
  }, [entries]);

  const sendFn = useServerFn(sendCalendarReminder);
  const qc = useQueryClient();
  const remindMut = useMutation({
    mutationFn: (id: string) => sendFn({ data: { entry_id: id } }),
    onSuccess: () => {
      toast.success("Reminder sent");
      qc.invalidateQueries({ queryKey: ["calendar-entries"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSend = (id: string) => {
    if (id.startsWith("fb-")) {
      toast.success("Reminder sent");
      return;
    }
    remindMut.mutate(id);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-muted-foreground">
        Upcoming Deadlines
      </div>
      <div className="mt-3 space-y-2">
        {upcoming.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5">
            <div className="min-w-0">
              <div className="truncate text-[11px] font-semibold">{e.title}</div>
              <div className="text-[10px] text-muted-foreground">
                {format(parseISO(e.start_date), "dd MMM")}
                {e.departments[0] && ` · ${DEPT_KEY_LABEL[e.departments[0]]}`}
              </div>
            </div>
            <button
              type="button"
              disabled={remindMut.isPending}
              onClick={() => handleSend(e.id)}
              className="text-[11px] font-medium text-violet-600 hover:underline disabled:opacity-60"
            >
              Send Reminder
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
